/* eslint-disable max-lines */
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormLabel from '@material-ui/core/FormLabel';
import LinearProgress from '@material-ui/core/LinearProgress';
import CustomMessage from '@react-form-fields/material-ui/components/CustomMessage';
import FieldDate from '@react-form-fields/material-ui/components/Date';
import FormValidation from '@react-form-fields/material-ui/components/FormValidation';
import FieldHidden from '@react-form-fields/material-ui/components/Hidden';
import Radio from '@react-form-fields/material-ui/components/Radio';
import FieldSelect from '@react-form-fields/material-ui/components/Select';
import FieldText from '@react-form-fields/material-ui/components/Text';
import { theme } from 'assets/theme';
import { FormComponent, IStateForm } from 'components/Abstract/Form';
import ErrorMessage from 'components/Shared/ErrorMessage';
import Snackbar from 'components/Shared/Snackbar';
import { WithStyles } from 'decorators/withStyles';
import { allowedRolesFor, IUser, ventureRelationType } from 'interfaces/user';
import { IUserRole } from 'interfaces/userRole';
import { IUserToken } from 'interfaces/userToken';
import { IVenture } from 'interfaces/venture';
import React, { Fragment } from 'react';
import { combineLatest } from 'rxjs';
import rxjsOperators from 'rxjs-operators';
import authService from 'services/auth';
import entityService from 'services/entity';
import userService from 'services/user';
import ventureService from 'services/venture';
import MenuItem from '@material-ui/core/MenuItem';
import { IEntity } from '../../../../../interfaces/entity';

/* eslint-disable max-lines */

interface IModel extends IUser {
  selectedVentures: number[];
  selectedEntities: number[];
}

interface IEntityOption {
  value: number,
  label: string,
  level: number,
  orderKey: string,
  disabled?: boolean,
  ventureId: number | number[]
}

interface IState extends IStateForm<IModel> {
  loading: boolean;
  roles: Array<IUserRole & { selected?: boolean }>;
  error?: null;
  ventures?: IVenture[];
  entityOptions: IEntityOption[];
}

interface IProps {
  opened: boolean;
  user?: IUser;
  onComplete: (user: IUser) => void;
  onCancel: () => void;
  classes?: any;
}

@WithStyles({
  content: {
    maxWidth: 'calc(95vw - 50px)'
  },
  heading: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  formRadioGroup: {
    marginTop: theme.spacing(3),
  },
  group: {
    margin: theme.spacing(1, 0),
  },
  formRadioDescription: {
    marginLeft: theme.spacing(4),
  },
  userRoleFormLabel: {
    marginBottom: theme.spacing(1),
  },
  dialogActions: {
    padding: theme.spacing(2)
  },
  moduleOption: {
    backgroundColor: theme.palette.primary.light,
    color: '#FFFFFF',
    '&:hover,&.Mui-selected': {
      color: '#000000'
    },
  }
})
export default class UserFormDialog extends FormComponent<IProps, IState> {
  constructor(props: {}) {
    super(props);
    this.state = {
      ...this.state,
      roles: [],
      loading: true,
      ventures: [],
      entityOptions: []
    };
  }

  get isEdit(): boolean {
    return !!this.state.model.id;
  }

  handleEnter = () => {
    const { user } = this.props;
    const selectedVentures = user && user.ventures ? user.ventures.map(({ id }) => id) : [] as number[];
    const selectedEntities = user && user.entities ? user.entities.map(({ id }) => id) : [] as number[];
    const model = {
      ...user,
      selectedVentures,
      selectedEntities
    };

    this.setState({ model });
    this.loadData();
    this.loadEntities();
  };

  handleExit = () => {
    this.resetForm();

    const roles = this.state.roles.map(r => ({ ...r, selected: false }));
    this.setState({ roles });
  };

  byUserRole = (loggedUser: IUserToken) => {
    const allowedRoles = allowedRolesFor(loggedUser);
    return (role: IUserRole & { selected: boolean }): boolean =>
      allowedRoles.findIndex(allowed => role.role === allowed) >= 0;
  };

  loadData = () => {
    this.setState({ loading: true, error: null });

    combineLatest([
      authService.getUser(),
      userService.roles(),
      ventureService.list({ page: 0, pageSize: 100 }),
    ]).pipe(
      rxjsOperators.logError(),
      rxjsOperators.bindComponent(this)
    ).subscribe(value => {
      const [loggedUser, roles, ventures] = value;
      const { data, updating } = roles;
      const { user } = this.props;

      this.setState({
        roles: (data || [])
          .map(r => ({ ...r, selected: !user ? false : user.roles.includes(r.role) }))
          .filter(this.byUserRole(loggedUser)),
        ventures: ventures.results,
        loading: updating
      });
    }, error => {
      this.setState({ loading: false, error });
    });
  };

  onSubmit = (isValid: boolean) => {
    const { model, roles } = this.state;
    const { onComplete } = this.props;

    if (!isValid) return;

    this.setState({ loading: true });
    model.roles = roles.filter(r => r.selected).map(r => r.role);

    userService.save(model as IModel).pipe(
      rxjsOperators.logError(),
      rxjsOperators.bindComponent(this)
    ).subscribe(user => {
      Snackbar.show(`${user.firstName} foi salvo${this.isEdit ? '' : ', um email foi enviado com a senha'}`);
      this.setState({ loading: false });

      onComplete(user);
    }, err => {
      Snackbar.error(err.message === 'email-unavailable' ? 'Email já utlizado' : err.message);
      this.setState({ loading: false });
    });
  };

  handleChange = (roleName: string) => {
    const roles = this.state.roles.map(role => {
      if (role.name === roleName) {
        return { ...role, selected: true };
      }
      return { ...role, selected: false };
    });
    this.setState({ roles });
  };

  ventureRelation = () => {
    const roleSelected = this.state.roles.filter(r => r.selected);
    if (roleSelected.length > 0)
      return ventureRelationType[roleSelected[0].role];
    return 0;
  };

  addIfNotExists = (array: IEntity[], current: IEntity, entity: IEntity & { disabled?: boolean }, uncheckDisabled?: boolean) => {
    if (!array.some(e => e.id === entity.id)) {
      array.push({
        ...entity,
        ventureName: entity.ventureId === current.ventureId ? current.ventureName : null
      });
    } else if (uncheckDisabled) {
      const addedEntity = array.find(e => e.id === entity.id);
      (addedEntity as any).disabled = false;
    }
  };

  loadEntities = () => {
    entityService.list({ page: 0, pageSize: Number.MAX_SAFE_INTEGER }).subscribe(graph => {
      graph.results = graph.results.reduce((prev, curr) => {
        this.addIfNotExists(prev, curr, curr, true);
        if (curr.parent) {
          this.addIfNotExists(prev, curr, {
            ...curr.parent,
            disabled: true
          });
          if (curr.parent.parent) {
            this.addIfNotExists(prev, curr, {
              ...curr.parent.parent,
              disabled: true
            });
          }
        }
        if (curr.children) {
          curr.children.forEach((ch) => {
            this.addIfNotExists(prev, curr, ch);
            if (ch.children) {
              ch.children.forEach((gCh) => {
                this.addIfNotExists(prev, curr, gCh);
              });
            }
          });
        }
        return prev;
      }, [] as IEntity[]);

      const entities = graph.results.filter(i => !i.parentId);
      const entityOptions = entities.reduce((prev, curr: IEntity & { disabled?: boolean }) => {
        const moduleOption = {
          value: curr.id,
          label: (curr.ventureName ? curr.ventureName + ' → ' : '') + curr.name,
          level: curr.level,
          ventureId: curr.ventureId,
          disabled: curr.disabled,
          orderKey: ((curr.ventureName ? curr.ventureName : '0') + '_' + curr.level + '_' + curr.name).replace(/ /g, '').toLowerCase()
        };
        prev.push(moduleOption);
        graph.results.filter(e => e.parentId === curr.id).forEach((t: IEntity & { disabled?: boolean }) => {
          const tabOption = {
            value: t.id,
            label: `${t.name}`,
            level: t.level,
            ventureId: t.ventureId,
            disabled: t.disabled,
            orderKey: (moduleOption.orderKey + '_' + t.level + '_' + t.name).replace(/ /g, '').toLowerCase()
          };
          prev.push(tabOption);
          graph.results.filter(e => e.parentId === t.id).forEach((st: IEntity & { disabled?: boolean }) =>
            prev.push({
              value: st.id,
              label: `${t.name} → ${st.name}`,
              level: st.level,
              ventureId: st.ventureId,
              disabled: st.disabled,
              orderKey: (tabOption.orderKey + '_' + st.level + '_' + st.name).replace(/ /g, '').toLowerCase()
            })
          );
        });

        return prev;
      }, [] as IEntityOption[]).sort((a, b) => {
        if (a.orderKey < b.orderKey) return -1;
        if (a.orderKey > b.orderKey) return 1;
        return 0;
      });

      this.setState({ entityOptions });
    }, e => console.log(e));
  };

  render() {
    const { model, loading, error, roles, ventures, entityOptions } = this.state;
    const { opened, classes, onCancel } = this.props;
    const roleRelation = this.ventureRelation();

    return (
      <Dialog
        open={opened}
        disableBackdropClick
        disableEscapeKeyDown
        onEnter={this.handleEnter}
        onExited={this.handleExit}
      >

        {loading && <LinearProgress color="secondary"/>}

        <FormValidation onSubmit={this.onSubmit} ref={this.formValidationRef}>
          <DialogTitle>{this.isEdit ? 'Editar' : 'Novo'} Usuário</DialogTitle>
          <DialogContent className={classes.content}>
            {error && <ErrorMessage error={error} tryAgain={this.loadData}/>}

            {!error &&
            <Fragment>
              <FieldText
                label="Nome"
                disabled={loading}
                value={model.firstName}
                validation="required|max:50"
                onChange={this.updateModel((model, v) => model.firstName = v)}
              />

              <FieldText
                label="Sobrenome"
                disabled={loading}
                value={model.lastName}
                validation="required|max:50"
                onChange={this.updateModel((model, v) => model.lastName = v)}
              />

              <FieldText
                label="Email"
                type="email"
                disabled={loading}
                value={model.email}
                validation="required|email|max:150"
                onChange={this.updateModel((model, v) => model.email = v)}
              />

              <FieldHidden
                value={roles.filter(r => r.selected).length}
                validation="required|numeric|min:1"
              >
                <CustomMessage rules="min,required,numeric">Selecione apenas um</CustomMessage>
              </FieldHidden>

              <Box display="flex" flexDirection="column" marginTop={2} marginBottom={1}>
                <FormLabel className={classes.userRoleFormLabel}>Perfil</FormLabel>
                {roles.map(role => (
                  <Radio
                    key={role.role}
                    checked={role.selected}
                    value={role.name}
                    label={role.name}
                    onChange={this.handleChange}
                  />
                ))}

                {roleRelation > 0 && (
                  <FieldSelect
                    label="Empreendimento"
                    value={model.selectedVentures}
                    options={ventures.sort((a, b) => {
                      if (a.name < b.name) return -1;
                      if (a.name > b.name) return 1;
                      return 0;
                    }).map(venture => ({ label: venture.name, value: venture.id }))}
                    validation="required"
                    onChange={this.updateModel((model, v) => model.selectedVentures = v)}
                    multiple
                  />
                )}

                {roleRelation >= 3 && (
                  <FieldSelect
                    label="Módulo"
                    value={model.selectedEntities}
                    validation="required"
                    onChange={this.updateModel((model, v) => model.selectedEntities = v)}
                    multiple
                  >
                    {entityOptions.filter(e => e.ventureId === null ||
                      (typeof e.ventureId === 'number' && model.selectedVentures.includes(e.ventureId))).map(entity => (
                      <MenuItem key={entity.value} value={entity.value} disabled={entity.disabled}
                        className={(entity.level === 0 ? classes.moduleOption : '') + ' ' + (entity.disabled ? 'Mui-disabled' : '')}>
                        {entity.label}
                      </MenuItem>
                    ))}
                  </FieldSelect>

                )}

                {roleRelation >= 4 && (
                  <FieldDate
                    label="Data de expiração"
                    className={classes.FieldText}
                    validation="required"
                    value={model.expirationDate}
                    onChange={this.updateModel((model, v) => model.expirationDate = v)}
                  />)
                }
              </Box>
            </Fragment>
            }
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button onClick={onCancel}>
              Cancelar
            </Button>
            <Button color="primary" variant={'contained'} type="submit" disabled={loading || !!error}>
              Salvar
            </Button>
          </DialogActions>
        </FormValidation>
      </Dialog>
    );
  }
}